import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, DollarSign, 
  Shield, Brain, TrendingUp, Cloud, 
  Check, ChevronRight, ChevronLeft
} from 'lucide-react';

// Subtle AdSense Component - TEMPORARILY DISABLED
/*
const SubtleAd = () => {
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, []);

  return (
    <div className="mt-8 py-4 px-4 bg-gradient-to-b from-gray-50 to-white rounded-lg">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3">
          <p className="text-xs text-gray-400 text-center mb-2">Advertisement</p>
          <ins className="adsbygoogle"
               style={{ display: 'block' }}
               data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
               data-ad-slot="XXXXXXXXXX"
               data-ad-format="auto"
               data-full-width-responsive="true"></ins>
        </div>
      </div>
    </div>
  );
};
*/

const IntakeLandingPage = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [clientIntake, setClientIntake] = useState({
    name: '',
    email: '',
    phone: '',
    selectedTier: '',
    agreementAccepted: false,
    paymentCompleted: false,
    signatureName: '',
    signatureDate: '',
    ipAddress: ''
  });

  const totalSteps = 4; // Agreement, Payment, Confirmation, Partner Options (Steps 0-4 = 5 total steps)
  const progressPercent = (currentStep / totalSteps) * 100;

  // Load saved form data and current step on mount
  useEffect(() => {
    // Check if returning from Stripe payment FIRST
    const urlParams = new URLSearchParams(window.location.search);
    const isReturningFromPayment = urlParams.get('payment') === 'success';
    
    const savedData = sessionStorage.getItem('intakeFormData');
    const savedStep = sessionStorage.getItem('intakeCurrentStep');
    
    // Helper function to get localStorage with 24-hour expiration
    const getFromLocalStorage = (key) => {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      
      try {
        const item = JSON.parse(itemStr);
        const now = new Date().getTime();
        const hoursDiff = (now - item.timestamp) / (1000 * 60 * 60);
        
        // Check if older than 24 hours
        if (hoursDiff > 24) {
          localStorage.removeItem(key);
          return null;
        }
        
        return item.value;
      } catch (e) {
        // If parsing fails, try to get as plain string (backwards compatibility)
        return itemStr;
      }
    };
    
    // Check for pre-filled data from intake.html or ContactPage (localStorage with 24-hour expiration)
    const prefilledName = getFromLocalStorage('userName');
    const prefilledEmail = getFromLocalStorage('userEmail');
    const prefilledPhone = getFromLocalStorage('userPhone');
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setClientIntake(parsedData);
      } catch (e) {
        console.error('Failed to restore form data:', e);
      }
    } else if (prefilledName || prefilledEmail || prefilledPhone) {
      // Pre-fill from localStorage if no saved session data
      setClientIntake(prev => ({
        ...prev,
        name: prefilledName || prev.name,
        email: prefilledEmail || prev.email,
        phone: prefilledPhone || prev.phone
      }));
    }
    
    // If returning from payment, go to step 3 (Confirmation)
    if (isReturningFromPayment) {
      setClientIntake(prev => ({ ...prev, paymentCompleted: true }));
      setCurrentStep(3);
      sessionStorage.setItem('intakeCurrentStep', '7');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } 
    // Otherwise, restore saved step
    else if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }
  }, []);

  // Save form data whenever it changes
  useEffect(() => {
    sessionStorage.setItem('intakeFormData', JSON.stringify(clientIntake));
  }, [clientIntake]);

  // Save current step whenever it changes and scroll to top
  useEffect(() => {
    sessionStorage.setItem('intakeCurrentStep', currentStep.toString());
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Submit intake data when moving to step 1 (Agreement - after tier selection)
    if (currentStep === 1 && clientIntake.selectedTier) {
      submitIntakeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  
  const submitIntakeData = async () => {
    try {
      const response = await fetch('https://4n7w60zube.execute-api.us-east-1.amazonaws.com/intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: clientIntake.name,
            email: clientIntake.email,
            phone: clientIntake.phone,
            selectedTier: clientIntake.selectedTier,
            agreementAccepted: clientIntake.agreementAccepted,
            signatureName: clientIntake.signatureName,
            signatureDate: clientIntake.signatureDate
          }
        })
      });
      
      const result = await response.json();
      console.log('Intake submission result:', result);
    } catch (error) {
      console.error('Failed to submit intake:', error);
      // Don't block the user if this fails - they can still proceed
    }
  };

  const updateField = (field, value) => {
    setClientIntake(prev => ({ ...prev, [field]: value }));
    
    // Save to localStorage with 24-hour expiration when name, email, or phone changes
    if (field === 'name' || field === 'email' || field === 'phone') {
      const item = {
        value: value,
        timestamp: new Date().getTime()
      };
      const storageKey = field === 'name' ? 'userName' : field === 'email' ? 'userEmail' : 'userPhone';
      localStorage.setItem(storageKey, JSON.stringify(item));
    }
  };



  const handleScheduleCall = () => {
    // Open Google Calendar in a new window so they can come back
    window.open('https://calendar.app.google/P1cvWEjf1XKdnyTm8', '_blank');
    // Automatically advance to Step 4 (Partner Options)
    setCurrentStep(4);
  };

  const canProceed = () => {
    if (currentStep === 0) {
      // Step 0: Pricing Tiers - need contact info and tier selection
      return clientIntake.name && clientIntake.email && clientIntake.phone && clientIntake.selectedTier;
    }
    if (currentStep === 1) {
      // Step 1: Agreement
      return clientIntake.agreementAccepted && clientIntake.signatureName && clientIntake.signatureName.trim().length > 2;
    }
    if (currentStep === 2) {
      // Step 2: Payment
      return clientIntake.paymentCompleted;
    }
    return true;
  };

  const getTierPrice = () => {
    const prices = { strategic: 99, growth: 249, sovereign: 599 };
    return prices[clientIntake.selectedTier] || 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <button 
                onClick={() => setCurrentStep(0)}
                className="mr-3 hover:opacity-80 transition"
              >
                <img 
                  src="/step0/Sovereign%20Stack%20Logo.png" 
                  alt="Sovereign Logic" 
                  className="w-10 h-10 rounded-lg object-contain"
                />
              </button>
              <h1 className="text-xl font-bold text-gray-800">Sovereign Logic <span className="text-base font-normal text-gray-600">by Larry Coons</span></h1>
            </div>
            <span className="text-sm text-gray-600">Step {currentStep} of {totalSteps}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-28 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Step 0: Pricing Tiers with Contact Info */}
          {currentStep === 0 && (
            <div className="animate-fadeIn">
              {/* Contact Information Section */}
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Let's Get Started</h2>
                <p className="text-gray-600 mb-8">Tell us about yourself</p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="inline w-4 h-4 mr-2" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={clientIntake.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="inline w-4 h-4 mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={clientIntake.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="inline w-4 h-4 mr-2" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={clientIntake.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Pricing Tiers Section */}
              <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Choose Your Plan</h2>
              <p className="text-gray-600 mb-12 text-center">Select the tier that best fits your needs</p>
              
              <div className="grid md:grid-cols-3 gap-6">
                {/* Strategic */}
                <PricingCard
                  title="Strategic"
                  price="99"
                  features={[
                    '1 hour-long strategy meeting per month',
                    'Basic bookkeeping and financial tracking',
                    'Email support between meetings',
                    'Chart of Accounts setup',
                    'Business compliance audit'
                  ]}
                  selected={clientIntake.selectedTier === 'strategic'}
                  onSelect={() => updateField('selectedTier', 'strategic')}
                />

                {/* Growth - Most Popular */}
                <PricingCard
                  title="Growth"
                  price="249"
                  popular
                  features={[
                    'Everything in Strategic',
                    'Weekly 1-hour strategy meetings',
                    'Advanced bookkeeping and financial analysis',
                    'Priority support and consulting',
                    'Custom dashboard and reports'
                  ]}
                  selected={clientIntake.selectedTier === 'growth'}
                  onSelect={() => updateField('selectedTier', 'growth')}
                  scale
                />

                {/* Sovereign */}
                <PricingCard
                  title="Sovereign"
                  price="599"
                  features={[
                    'Everything in Growth',
                    'Full-charge dedicated bookkeeper',
                    'Daily strategy touchpoints',
                    'White-glove service and on-demand consulting',
                    'Custom integrations and development'
                  ]}
                  selected={clientIntake.selectedTier === 'sovereign'}
                  onSelect={() => updateField('selectedTier', 'sovereign')}
                />
              </div>
            </div>
          )}

          {/* Step 1: Client Agreement */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 animate-fadeIn max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Consulting Services Agreement</h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6 max-h-96 overflow-y-auto text-left text-sm">
                <div className="prose prose-sm max-w-none">
                  <h3 className="font-bold text-lg mb-3">SOVEREIGN LOGIC CONSULTING SERVICES AGREEMENT</h3>
                  
                  <p className="mb-3"><strong>Selected Tier:</strong> {clientIntake.selectedTier?.toUpperCase()} - ${getTierPrice()}/month</p>
                  
                  <h4 className="font-semibold mt-4 mb-2">1. SERVICES</h4>
                  <p className="text-gray-700 mb-3">
                    Consultant agrees to provide Platform as a Service (PaaS) consulting, bookkeeping, and implementation services as outlined in your selected tier:
                  </p>
                  <p className="text-gray-700 mb-2"><strong>Strategic ($99/month):</strong> 1 hour-long strategy meeting per month, basic bookkeeping and financial tracking, email support, chart of accounts setup, business compliance audit</p>
                  <p className="text-gray-700 mb-2"><strong>Growth ($249/month):</strong> Everything in Strategic + Weekly 1-hour strategy meetings, advanced bookkeeping and financial analysis, priority support and consulting, custom dashboard and reports</p>
                  <p className="text-gray-700 mb-3"><strong>Sovereign ($599/month):</strong> Everything in Growth + Full-charge dedicated bookkeeper, daily strategy touchpoints, white-glove service and on-demand consulting, custom integrations and development</p>

                  <h4 className="font-semibold mt-4 mb-2">2. PAYMENT TERMS</h4>
                  <p className="text-gray-700 mb-2"><strong>Monthly Fee:</strong> ${getTierPrice()}/month (includes regular consulting time as described)</p>
                  <p className="text-gray-700 mb-2"><strong>Additional Consulting Hours:</strong> $99/hour (billed separately for time outside your tier's included hours)</p>
                  <p className="text-gray-700 mb-2"><strong>Custom Development:</strong> $99/hour (10-40 hours typical, based on scope and complexity)</p>
                  <p className="text-gray-700 mb-2"><strong>Development Financing:</strong> Net 30 with 3% terms. Minimum monthly payment of 15% of outstanding balance required while maintaining active subscription.</p>
                  <p className="text-gray-700 mb-2"><strong>Payment:</strong> Due monthly, starting today</p>
                  <p className="text-red-600 font-semibold mb-3">⚠️ ALL PAYMENTS ARE NON-REFUNDABLE</p>
                  <p className="text-gray-700 mb-3">You may be responsible for third-party service costs (QuickBooks, AWS, Shopify, etc.) billed directly by those providers.</p>

                  <h4 className="font-semibold mt-4 mb-2">3. INTELLECTUAL PROPERTY & OWNERSHIP</h4>
                  <p className="text-gray-700 mb-2">
                    During the development financing period, Consultant retains ownership of custom  development work, URLs, and codebase. You receive a non-exclusive license to use the platform for your business.
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>OWNERSHIP TRANSFER:</strong> Upon full payment of development balance, Consultant will permanently transfer complete ownership of the URL, domain, and codebase directly to your business.
                  </p>

                  <h4 className="font-semibold mt-4 mb-2">4. TERMINATION</h4>
                  <p className="text-gray-700 mb-3">
                    Either party may terminate with 30 days notice. No refunds for current billing period. Upon termination, you must cease using all Consultant intellectual property.
                  </p>

                  <h4 className="font-semibold mt-4 mb-2">5. LIMITATION OF LIABILITY</h4>
                  <p className="text-red-600 font-semibold mb-2">
                    ⚠️ SERVICES PROVIDED "AS IS" WITHOUT WARRANTY
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>Maximum Liability:</strong> Limited to fees paid in the prior 3 months.
                    <br/><strong>Excluded:</strong> No liability for indirect damages, lost profits, data loss, or third-party service failures (AWS, Stripe, etc.).
                  </p>

                  <h4 className="font-semibold mt-4 mb-2">6. YOUR LEGAL RESPONSIBILITY</h4>
                  <p className="text-red-600 font-semibold mb-2">
                    ⚠️ YOU ARE SOLELY RESPONSIBLE FOR YOUR CONTENT AND LEGAL COMPLIANCE
                  </p>
                  <p className="text-gray-700 mb-2">
                    You are responsible for: (a) all content on your site, (b) ensuring your business complies with all laws, (c) obtaining necessary licenses and permits, (d) your business practices and customer disputes.
                  </p>
                  <p className="text-gray-700 mb-3">
                    <strong>No Legal Advice:</strong> We provide technical consulting only, not legal, tax, or financial advice. Consult licensed professionals for legal matters.
                  </p>

                  <h4 className="font-semibold mt-4 mb-2">7. INDEMNIFICATION</h4>
                  <p className="text-gray-700 mb-3">
                    You agree to indemnify and hold Consultant harmless from any claims arising from: your use of services, violation of laws, illegal activities, your content or business practices, or breach of this agreement.
                  </p>

                  <h4 className="font-semibold mt-4 mb-2">8. DISPUTE RESOLUTION</h4>
                  <p className="text-gray-700 mb-3">
                    <strong>Governing Law:</strong> Nevada, USA
                    <br/><strong>Disputes:</strong> Resolved through binding arbitration in Clark County, Nevada
                    <br/><strong>Attorney Fees:</strong> Prevailing party recovers costs
                  </p>

                  <p className="text-xs text-gray-500 mt-6 italic">
                    Full agreement available at: www.larrycoons.com/intake/agreement
                  </p>
                </div>
              </div>

              {/* Signature Section */}
              <div className="bg-blue-50 border-2 border-blue-600 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-gray-800 mb-4">Electronic Signature</h3>
                <p className="text-sm text-gray-700 mb-4">
                  By typing your full legal name below, you are electronically signing this agreement and affirming that you have the authority to enter into this legally binding contract.
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Legal Name (Electronic Signature) *
                  </label>
                  <input
                    type="text"
                    value={clientIntake.signatureName}
                    onChange={(e) => {
                      updateField('signatureName', e.target.value);
                      if (!clientIntake.signatureDate) {
                        updateField('signatureDate', new Date().toISOString());
                      }
                    }}
                    placeholder="Type your full legal name"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 font-serif text-lg"
                    style={{ fontFamily: 'Brush Script MT, cursive' }}
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-start cursor-pointer bg-white p-3 rounded border-2 border-gray-300">
                    <input
                      type="checkbox"
                      checked={clientIntake.agreementAccepted}
                      onChange={(e) => updateField('agreementAccepted', e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                    />
                    <span className="text-sm text-gray-700">
                      I have read and agree to the Consulting Services Agreement above, including the <strong>non-refundable payment policy</strong>, <strong>limitation of liability</strong>, <strong>binding arbitration in Nevada</strong>, and that I am <strong>solely responsible for my content and legal compliance</strong>. I understand that Consultant retains all intellectual property rights and provides technical services only, not legal advice.
                    </span>
                  </label>
                </div>

                {clientIntake.signatureDate && (
                  <div className="text-xs text-gray-600 bg-gray-100 p-3 rounded">
                    <strong>Signed:</strong> {new Date(clientIntake.signatureDate).toLocaleString()}
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-sm text-gray-800">
                  <strong>⚠️ Legal Notice:</strong> This is a legally binding contract. Your electronic signature has the same legal effect as a handwritten signature. By proceeding, you acknowledge that you have read, understood, and agree to all terms.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Payment */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 animate-fadeIn">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <DollarSign className="w-10 h-10 text-blue-600" />
                </div>
                
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Complete Your Payment</h2>
                <p className="text-gray-600 mb-8">
                  Secure payment processing through Stripe
                </p>
              </div>

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8 max-w-md mx-auto">
                <h3 className="font-semibold text-gray-800 mb-4">Payment Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium capitalize">{clientIntake.selectedTier} Tier</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Billing:</span>
                    <span className="font-medium">Monthly</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold">First Payment:</span>
                    <span className="font-bold text-xl text-blue-600">${getTierPrice()}</span>
                  </div>
                </div>
              </div>

              {/* Stripe Payment Button */}
              <div className="max-w-md mx-auto">
                <button
                  onClick={() => {
                    // Direct Stripe Payment Links by tier
                    const paymentLinks = {
                      starter: 'https://buy.stripe.com/6oU9AU2GlgNn6wpcoa2cg00',
                      creator: 'https://buy.stripe.com/28E28s94JeFf9IB2NA2cg01',
                      partner: 'https://buy.stripe.com/bJe9AU94JaoZdYRgEq2cg02'
                    };
                    
                    const paymentUrl = paymentLinks[clientIntake.selectedTier];
                    
                    if (paymentUrl) {
                      // Redirect to Stripe Payment Link
                      window.location.href = paymentUrl;
                    } else {
                      alert('Please select a tier before proceeding to payment.');
                    }
                  }}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center"
                >
                  <DollarSign className="w-6 h-6 mr-2" />
                  Pay ${getTierPrice()} with Stripe
                </button>

                <div className="mt-6 flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>Secure payment powered by Stripe</span>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Your payment information is encrypted and secure. You'll receive a receipt via email.
                </p>

                {/* Promo Code Link */}
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setShowPromoCode(!showPromoCode)}
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                  >
                    {showPromoCode ? 'Hide promo code' : 'Have a promo code?'}
                  </button>
                </div>

                {/* Promo Code Input */}
                {showPromoCode && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Promo Code
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoError('');
                        }}
                        placeholder=""
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                        disabled={promoLoading}
                      />
                      <button
                        onClick={async () => {
                          if (!promoCode.trim()) {
                            setPromoError('Please enter a promo code');
                            return;
                          }

                          setPromoLoading(true);
                          setPromoError('');

                          try {
                            const response = await fetch('https://4n7w60zube.execute-api.us-east-1.amazonaws.com/payment', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                tier: clientIntake.selectedTier,
                                email: clientIntake.email,
                                name: clientIntake.name,
                                signatureName: clientIntake.signatureName,
                                signatureDate: clientIntake.signatureDate,
                                promoCode: promoCode
                              })
                            });

                            const data = await response.json();

                            if (data.success) {
                              // Promo code valid - skip payment and go to confirmation
                              updateField('paymentCompleted', true);
                              sessionStorage.setItem('intakeCurrentStep', '3');
                              setCurrentStep(3);
                            } else {
                              setPromoError(data.error || 'Invalid promo code. Please try again.');
                            }
                          } catch (error) {
                            console.error('Promo code error:', error);
                            setPromoError('Unable to validate promo code. Please try again.');
                          } finally {
                            setPromoLoading(false);
                          }
                        }}
                        disabled={promoLoading}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {promoLoading ? 'Validating...' : 'Apply'}
                      </button>
                    </div>
                    {promoError && (
                      <p className="mt-2 text-sm text-red-600">{promoError}</p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Demo codes allow you to skip payment for testing purposes
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Confirmation & Calendar */}
          {currentStep === 3 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 animate-fadeIn">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Welcome to Sovereign Logic!</h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Your payment has been processed successfully. Let's schedule your onboarding consultation.
                </p>

                {/* Success Summary */}
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-8 text-left max-w-2xl mx-auto">
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <Check className="w-5 h-5 text-green-600 mr-2" />
                    Order Confirmation
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Client:</span>
                      <span className="font-medium">{clientIntake.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium">{clientIntake.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plan:</span>
                      <span className="font-medium capitalize">{clientIntake.selectedTier} - ${getTierPrice()}/month</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-green-600">✓ Payment Confirmed</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next Billing:</span>
                      <span className="font-medium">{new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="bg-blue-50 rounded-xl p-6 mb-8 max-w-2xl mx-auto text-left">
                  <h3 className="font-semibold text-gray-800 mb-4">What Happens Next?</h3>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 font-bold">1.</span>
                      <span>
                        Please setup your{' '}
                        <a href="https://referworkspace.app.goo.gl/psSz" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Workspace</a>,{' '}
                        <a href="https://shopify.pxf.io/larrycoons" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Shopify</a>,{' '}
                        <a href="https://monday.grsm.io/larrycoons" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Monday</a>,{' '}
                        and QuickBooks accounts
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 font-bold">2.</span>
                      <span>Schedule your Onboarding call (see below for the button)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 font-bold">3.</span>
                      <span>Plan your platform and chart of accounts, review them</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 font-bold">4.</span>
                      <span>Attend project meetings to setup your business or platform</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 font-bold">5.</span>
                      <span>Meet with me regularly so we can improve your platform</span>
                    </li>
                  </ul>
                </div>

                {/* FTC Compliance Disclosure */}
                <div className="mb-6 max-w-2xl mx-auto">
                  <p className="text-xs text-gray-600 text-center">
                    <strong>Disclosure:</strong> We may earn a commission from qualifying purchases made through referral links on this page. 
                    See our <a href="/affiliate-disclosure.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Affiliate Disclosure</a> for more information.
                  </p>
                </div>

                <button
                  onClick={handleScheduleCall}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Schedule Your Onboarding Call
                  <ChevronRight className="inline w-5 h-5 ml-2" />
                </button>

                <p className="text-xs text-gray-500 mt-4">
                  Opens Google Calendar in a new tab. After scheduling, continue to explore partner options.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Partner Options */}
          {currentStep === 4 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Explore Our Partner Ecosystem</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Sovereign Logic partners with leading platforms to provide comprehensive solutions for your business.
                </p>
              </div>

              {/* Partner Categories */}
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {/* Monday.com CRM Partner */}
                <a href="https://try.monday.com/phnz5wd5z0bg" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 transition block">
                  <div className="flex items-center mb-4">
                    <Brain className="w-8 h-8 text-purple-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-800">Sales Growth with Monday.com</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Manage your sales pipeline, track deals, and grow revenue with Monday.com's intuitive CRM platform.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Visual sales pipeline management</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Contact & lead tracking</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Automated email sequences</span>
                    </li>
                  </ul>
                </a>

                {/* Google Workspace */}
                <a href="https://c.gle/ANnMTcaSezQCx918VcW4en9LOgLK2rGZnDhG4dU5NVdsxkqJtzMKkWayRK9iVhjy-uGVpbpPGHbcjeKdtJjNCTbpkIeDv3Gp0WYXooa-FhlNpR4e_BmutHiqZKIUYNM3Iq_ekehyJIHIn0nLh9HUV5XN" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 transition block">
                  <div className="flex items-center mb-4">
                    <TrendingUp className="w-8 h-8 text-orange-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-800">Cloud Workspace</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Professional email and collaboration tools powered by Google Workspace.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Custom business email addresses</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Google Drive, Docs, Sheets & more</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Video conferencing with Google Meet</span>
                    </li>
                  </ul>
                </a>

                {/* e-Commerce Solutions - Shopify */}
                <a href="https://shopify.pxf.io/POo5bR" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 transition block">
                  <div className="flex items-center mb-4">
                    <DollarSign className="w-8 h-8 text-green-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-800">e-Commerce Solutions</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Build and scale your online store with Shopify.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Complete online store in minutes</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Built-in payment processing & shipping</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>1,000+ apps and themes available</span>
                    </li>
                  </ul>
                </a>

                {/* Business Culture - Audible */}
                <a href="https://www.amazon.com/Audible-Books-and-Originals/b?ie=UTF8&node=18145289011&linkCode=ll2&tag=larrycoons-20&linkId=868a673fcac0aff9727f933c8fa369ed&language=en_US&ref_=as_li_ss_tl" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-400 transition block">
                  <div className="flex items-center mb-4">
                    <Cloud className="w-8 h-8 text-blue-600 mr-3" />
                    <h3 className="text-xl font-semibold text-gray-800">Business Culture</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Grow your business mindset with Audible's audiobook library of business classics and modern insights.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>1,000+ business & leadership titles</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Learn while driving or working out</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>30-day free trial available</span>
                    </li>
                  </ul>
                </a>
              </div>

              {/* Thank You Message */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 mt-8 max-w-3xl mx-auto text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Thank You for Subscribing!</h3>
                <p className="text-gray-600 mb-4">
                  Welcome to Sovereign Logic, {clientIntake.name}! Check your email for next steps and onboarding details.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  We'll discuss these partner options during your onboarding call.
                </p>
                
                {/* Planning Tool CTA */}
                <a
                  href="https://www.larrycoons.com/step0/?view=planning"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Start Planning Your Platform
                  <ChevronRight className="w-6 h-6 ml-2" />
                </a>
                <p className="text-xs text-gray-500 mt-4">
                  Access our free planning tools: Auto-Stack Advisor, Profit Calculator, Value Gap Analysis & more
                </p>
              </div>
            </div>
          )}

          {/* Subtle Ad - Shows on all steps - TEMPORARILY DISABLED */}
          {/* {currentStep > 0 && currentStep < totalSteps && <SubtleAd />} */}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="flex items-center px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Back
            </button>

            {currentStep < totalSteps && (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceed()}
                className="flex items-center px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              >
                Continue
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Larry Coons</h3>
              <p className="text-gray-400">Enterprise solutions for entrepreneurs.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/step0/" className="hover:text-white transition">Sovereign Logic</a></li>
                <li><a href="/#services" className="hover:text-white transition">Consulting</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Content</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://www.getridehaul.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Ridehaul</a></li>
                <li><a href="https://open.spotify.com/show/4tLhN9WS6QA662XSkbPPyi" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Podcast</a></li>
                <li><a href="https://larrycoons.substack.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Newsletter</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/privacy-policy.html" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/terms-of-service.html" className="hover:text-white transition">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Larry Coons. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Pricing Card Component
const PricingCard = ({ title, price, originalPrice, savings, features, popular, selected, onSelect, scale }) => {
  return (
    <div
      className={`relative bg-white rounded-xl border-2 p-6 transition-all cursor-pointer ${
        selected
          ? 'border-blue-600 shadow-xl'
          : 'border-gray-200 hover:border-blue-300 shadow-md'
      } ${scale ? 'md:scale-105 md:z-10' : ''}`}
      onClick={onSelect}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{title}</h3>
        {originalPrice && savings && (
          <div className="mb-2">
            <span className="text-lg text-gray-400 line-through">${originalPrice}</span>
            <span className="ml-2 text-sm font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">Save ${savings}</span>
          </div>
        )}
        <div className="flex items-baseline justify-center">
          <span className="text-4xl font-bold text-gray-900">${price}</span>
          <span className="text-gray-600 ml-2">/month</span>
        </div>
        {originalPrice && (
          <p className="text-xs text-gray-500 mt-1">Special Offer</p>
        )}
      </div>

      <ul className="space-y-3 mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start text-sm">
            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        className={`w-full py-3 rounded-lg font-semibold transition ${
          selected
            ? 'bg-blue-600 text-white shadow-lg'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {selected ? 'Selected ✓' : 'Select Tier'}
      </button>
    </div>
  );
};

export default IntakeLandingPage;
